import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { TrendingUp, TrendingDown, RefreshCw, ArrowUpDown } from 'lucide-react'
import { api } from '@/lib/api'
import { formatCurrency, formatPercent, cn } from '@/lib/utils'
import type { Holding, PortfolioSummary } from '@/types'

type SortKey = 'symbol' | 'shares' | 'total_value' | 'unrealized_pnl' | 'pnl_pct'
type SortDir = 'asc' | 'desc'

export default function Portfolio() {
  const navigate = useNavigate()
  const [holdings, setHoldings] = useState<Holding[]>([])
  const [summary, setSummary] = useState<PortfolioSummary | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [sortKey, setSortKey] = useState<SortKey>('total_value')
  const [sortDir, setSortDir] = useState<SortDir>('desc')

  const load = async (refreshing = false) => {
    if (refreshing) setIsRefreshing(true)
    else setIsLoading(true)

    const res = await api.getPortfolio()
    if (res.success && res.data) {
      setHoldings(res.data.holdings)
      setSummary(res.data.summary)
    }

    setIsLoading(false)
    setIsRefreshing(false)
  }

  useEffect(() => { load() }, [])

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => d === 'asc' ? 'desc' : 'asc')
    } else {
      setSortKey(key)
      setSortDir('desc')
    }
  }

  const sorted = [...holdings].sort((a, b) => {
    if (sortKey === 'symbol') {
      return sortDir === 'asc' ? a.symbol.localeCompare(b.symbol) : b.symbol.localeCompare(a.symbol)
    }
    if (sortKey === 'shares') {
      return sortDir === 'asc' ? a.shares - b.shares : b.shares - a.shares
    }
    if (sortKey === 'total_value') {
      const aVal = a.total_value ?? 0
      const bVal = b.total_value ?? 0
      return sortDir === 'asc' ? aVal - bVal : bVal - aVal
    }
    if (sortKey === 'unrealized_pnl') {
      const aVal = a.unrealized_pnl ?? 0
      const bVal = b.unrealized_pnl ?? 0
      return sortDir === 'asc' ? aVal - bVal : bVal - aVal
    }
    if (sortKey === 'pnl_pct') {
      const aVal = a.avg_cost_basis > 0 ? (((a.current_price ?? a.avg_cost_basis) - a.avg_cost_basis) / a.avg_cost_basis) : 0
      const bVal = b.avg_cost_basis > 0 ? (((b.current_price ?? b.avg_cost_basis) - b.avg_cost_basis) / b.avg_cost_basis) : 0
      return sortDir === 'asc' ? aVal - bVal : bVal - aVal
    }
    return 0
  })

  if (isLoading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-8 bg-white/[0.06] rounded-xl w-40" />
        <div className="grid grid-cols-3 gap-4">
          {[...Array(3)].map((_, i) => <div key={i} className="h-24 bg-white/[0.04] rounded-xl" />)}
        </div>
        <div className="h-72 bg-white/[0.04] rounded-xl" />
      </div>
    )
  }

  const SortHeader = ({ label, sortK }: { label: string; sortK: SortKey }) => (
    <th
      onClick={() => handleSort(sortK)}
      className="px-4 py-3 text-left text-xs font-medium text-white/30 uppercase tracking-wider cursor-pointer hover:text-white/60 transition-colors select-none"
    >
      <span className="flex items-center gap-1">
        {label}
        <ArrowUpDown className={cn('w-3 h-3', sortKey === sortK ? 'text-accent' : 'text-white/20')} />
      </span>
    </th>
  )

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Portfolio</h1>
          <p className="text-white/40 text-sm mt-0.5">Your current holdings</p>
        </div>
        <button
          onClick={() => load(true)}
          disabled={isRefreshing}
          className="btn-secondary text-sm flex items-center gap-2"
        >
          <RefreshCw className={cn('w-4 h-4', isRefreshing && 'animate-spin')} />
          Refresh prices
        </button>
      </div>

      {/* Summary */}
      {summary && (
        <div className="grid grid-cols-3 gap-4">
          <div className="card p-5">
            <div className="text-xs text-white/30 uppercase tracking-wider mb-2">Total Value</div>
            <div className="text-xl font-semibold font-mono">{formatCurrency(summary.total_value)}</div>
          </div>
          <div className="card p-5">
            <div className="text-xs text-white/30 uppercase tracking-wider mb-2">Holdings Value</div>
            <div className="text-xl font-semibold font-mono">{formatCurrency(summary.holdings_value)}</div>
          </div>
          <div className="card p-5">
            <div className="text-xs text-white/30 uppercase tracking-wider mb-2">Total Return</div>
            <div
              className={cn(
                'text-xl font-semibold font-mono',
                summary.total_return >= 0 ? 'text-gain' : 'text-loss'
              )}
            >
              {formatPercent(summary.total_return_pct)}
            </div>
            <div className="text-xs text-white/30 mt-0.5">{formatCurrency(summary.total_return)}</div>
          </div>
        </div>
      )}

      {/* Holdings table */}
      <div className="card overflow-hidden">
        {sorted.length === 0 ? (
          <div className="px-6 py-12 text-center">
            <div className="text-white/30 text-sm mb-3">No holdings yet</div>
            <button onClick={() => navigate('/trade')} className="btn-primary text-sm py-2">
              Start Trading
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="border-b border-white/[0.06]">
                <tr>
                  <SortHeader label="Symbol" sortK="symbol" />
                  <SortHeader label="Shares" sortK="shares" />
                  <th className="px-4 py-3 text-left text-xs font-medium text-white/30 uppercase tracking-wider">
                    Avg Cost
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-white/30 uppercase tracking-wider">
                    Current Price
                  </th>
                  <SortHeader label="Total Value" sortK="total_value" />
                  <SortHeader label="Unrealized P/L" sortK="unrealized_pnl" />
                  <SortHeader label="Return %" sortK="pnl_pct" />
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-white/[0.04]">
                {sorted.map((h) => {
                  const pnlPct = h.avg_cost_basis > 0
                    ? (((h.current_price ?? h.avg_cost_basis) - h.avg_cost_basis) / h.avg_cost_basis) * 100
                    : 0
                  const isUp = (h.unrealized_pnl ?? 0) >= 0

                  return (
                    <tr key={h.symbol} className="hover:bg-white/[0.02] transition-colors">
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-white/[0.06] rounded-lg flex items-center justify-center flex-shrink-0">
                            <span className="text-xs font-bold text-white/60">{h.symbol.slice(0, 2)}</span>
                          </div>
                          <span className="font-semibold text-sm">{h.symbol}</span>
                        </div>
                      </td>
                      <td className="px-4 py-4 font-mono text-sm">{h.shares.toLocaleString()}</td>
                      <td className="px-4 py-4 font-mono text-sm text-white/60">
                        {formatCurrency(h.avg_cost_basis)}
                      </td>
                      <td className="px-4 py-4 font-mono text-sm">
                        {h.current_price ? formatCurrency(h.current_price) : '—'}
                      </td>
                      <td className="px-4 py-4 font-mono text-sm font-medium">
                        {formatCurrency(h.total_value ?? 0)}
                      </td>
                      <td className={cn('px-4 py-4 font-mono text-sm', isUp ? 'text-gain' : 'text-loss')}>
                        {isUp ? '+' : ''}{formatCurrency(h.unrealized_pnl ?? 0)}
                      </td>
                      <td className={cn('px-4 py-4 text-sm', isUp ? 'text-gain' : 'text-loss')}>
                        <span className="flex items-center gap-1">
                          {isUp ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                          {formatPercent(pnlPct)}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        <button
                          onClick={() => navigate(`/trade/${h.symbol}`)}
                          className="text-xs text-accent hover:underline"
                        >
                          Trade
                        </button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
